import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, LogOut, Mail } from 'lucide-react';
import { useUser, useAuthStore } from '../store/useAuthStore';
import { useTenantBranding } from '../hooks/useTenantBranding-runtime';

export const PendingApprovalDashboard: React.FC = () => {
  const navigate = useNavigate();
  const user = useUser();
  const { logout } = useAuthStore();
  const { branding, colors } = useTenantBranding();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--neutral-100)' }}>
      {/* Header */}
      <header className="shadow-sm" style={{ background: 'var(--surface-primary)', borderBottom: '1px solid var(--neutral-200)' }}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg p-1" style={{ background: 'var(--surface-primary)' }}>
              <img
                src={branding.logo || "/5-axis-logo.svg"}
                alt={`${branding.companyName} Logo`}
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <span className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{branding.title}</span>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center px-4 py-2 text-sm font-medium rounded-lg hover:bg-gray-100 transition-colors"
            style={{ color: 'var(--text-secondary)' }}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="max-w-lg w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center" style={{ border: '1px solid var(--neutral-200)' }}>
            {/* Icon */}
            <div
              className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${colors.primary}20, ${colors.lighter}30)` }}
            >
              <Clock className="w-10 h-10" style={{ color: colors.primary }} />
            </div>

            {/* Title */}
            <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
              Account Pending Approval
            </h1>

            {/* User Info */}
            {user && (
              <p className="text-lg mb-4" style={{ color: 'var(--text-secondary)' }}>
                Hello, {user.profile?.firstName || 'there'}!
              </p>
            )}

            {/* Message */}
            <p className="mb-6" style={{ color: 'var(--text-tertiary)' }}>
              Your account is currently under review. An administrator will review and approve your registration shortly.
            </p>

            {/* Status Badge */}
            <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-amber-50 text-amber-700 border border-amber-200 mb-6">
              <Clock className="w-4 h-4 mr-2" />
              Pending Review
            </div>

            {/* What to expect */}
            <div className="text-left bg-gray-50 rounded-xl p-5 mb-6" style={{ border: '1px solid var(--neutral-200)' }}>
              <h3 className="font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
                What happens next?
              </h3>
              <ul className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                <li className="flex items-start">
                  <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold mr-3 mt-0.5 flex-shrink-0">1</span>
                  <span>An administrator will review your registration details</span>
                </li>
                <li className="flex items-start">
                  <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold mr-3 mt-0.5 flex-shrink-0">2</span>
                  <span>You'll receive an email notification once approved</span>
                </li>
                <li className="flex items-start">
                  <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold mr-3 mt-0.5 flex-shrink-0">3</span>
                  <span>After approval, you'll have full access to the platform</span>
                </li>
              </ul>
            </div>

            {/* Contact Support */}
            <div className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
              <p className="mb-2">Questions? Contact our support team:</p>
              <div className="flex items-center justify-center space-x-4">
                <a
                  href="mailto:support@5axishealth.com"
                  className="inline-flex items-center hover:underline"
                  style={{ color: colors.primary }}
                >
                  <Mail className="w-4 h-4 mr-1" />
                  support@5axishealth.com
                </a>
              </div>
            </div>
          </div>

          {/* Footer note */}
          <p className="text-center text-xs mt-4" style={{ color: 'var(--text-tertiary)' }}>
            You can safely close this page. We'll notify you by email when your account is approved.
          </p>
        </div>
      </main>
    </div>
  );
};

export default PendingApprovalDashboard;
